import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageCircle, Settings, Key, Send, CheckCircle, 
  AlertCircle, Loader2, Eye, EyeOff, Phone, Users,
  Megaphone, Bell, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  
  const [config, setConfig] = useState({
    is_enabled: false,
    provider: 'meta',
    // Meta
    meta_phone_number_id: '',
    meta_access_token: '',
    meta_business_account_id: '',
    // Twilio
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_number: '',
    // Interakt
    interakt_api_key: '',
    // AiSensy
    aisensy_api_key: '',
    aisensy_partner_id: '',
    // Notifications
    send_payment_confirmation: true,
    send_payment_approval: true,
    send_payment_rejection: true,
    send_expiry_reminder: true,
    send_welcome_message: true,
    // Templates
    template_payment_confirmation: 'payment_confirmation',
    template_payment_approval: 'payment_approval',
    template_payment_rejection: 'payment_rejection',
    template_expiry_reminder: 'subscription_expiry',
    template_welcome: 'welcome_tenant'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/whatsapp/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setConfig(prev => ({ ...prev, ...data.settings }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/whatsapp/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('WhatsApp settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: testPhone })
      });
      
      if (response.ok) {
        toast.success('Test message sent! Check WhatsApp.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test message');
      }
    } catch (error) {
      toast.error('Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setBroadcasting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/whatsapp/broadcast`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          message: broadcastMessage,
          include_groups: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Broadcast sent: ${data.sent_count}/${data.total} delivered`);
        setShowBroadcast(false);
        setBroadcastMessage('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send broadcast');
      }
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setBroadcasting(false);
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const providers = [
    { id: 'meta', name: 'WhatsApp Cloud API (Meta)', icon: '📱', description: 'Official Meta API - Free tier available' },
    { id: 'twilio', name: 'Twilio WhatsApp', icon: '🔴', description: 'Easy integration - Pay per message' },
    { id: 'interakt', name: 'Interakt', icon: '💬', description: 'Indian provider - Affordable pricing' },
    { id: 'aisensy', name: 'AiSensy', icon: '🤖', description: 'Indian provider - Good support' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="h-7 w-7 text-green-600" />
            WhatsApp Settings
          </h1>
          <p className="text-gray-500">Configure WhatsApp for sending notifications to tenants</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowBroadcast(true)} variant="outline" disabled={!config.is_enabled}>
            <Megaphone className="h-4 w-4 mr-2" />
            Broadcast
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Enable/Disable */}
      <Card className="border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">WhatsApp Notifications</p>
                <p className="text-sm text-gray-500">Enable WhatsApp messaging for tenants</p>
              </div>
            </div>
            <Switch
              checked={config.is_enabled}
              onCheckedChange={(checked) => setConfig({ ...config, is_enabled: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {config.is_enabled && (
        <Tabs defaultValue="provider" className="space-y-4">
          <TabsList>
            <TabsTrigger value="provider">Provider Setup</TabsTrigger>
            <TabsTrigger value="notifications">Notification Types</TabsTrigger>
            <TabsTrigger value="test">Test & Send</TabsTrigger>
          </TabsList>

          {/* Provider Setup Tab */}
          <TabsContent value="provider" className="space-y-4">
            {/* Provider Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select WhatsApp Provider</CardTitle>
                <CardDescription>Choose your preferred WhatsApp Business API provider</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {providers.map((provider) => (
                    <div
                      key={provider.id}
                      onClick={() => setConfig({ ...config, provider: provider.id })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        config.provider === provider.id 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{provider.icon}</span>
                        <div>
                          <p className="font-medium">{provider.name}</p>
                          <p className="text-sm text-gray-500">{provider.description}</p>
                        </div>
                        {config.provider === provider.id && (
                          <CheckCircle className="h-5 w-5 text-green-600 ml-auto" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Provider-specific Configuration */}
            {config.provider === 'meta' && (
              <Card className="border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    📱 Meta WhatsApp Cloud API
                    <Badge variant="outline">Official</Badge>
                  </CardTitle>
                  <CardDescription>
                    Get credentials from <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone Number ID *</Label>
                      <Input
                        value={config.meta_phone_number_id}
                        onChange={(e) => setConfig({ ...config, meta_phone_number_id: e.target.value })}
                        placeholder="1234567890123456"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Business Account ID</Label>
                      <Input
                        value={config.meta_business_account_id}
                        onChange={(e) => setConfig({ ...config, meta_business_account_id: e.target.value })}
                        placeholder="1234567890123456"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Access Token *</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets.meta_access_token ? 'text' : 'password'}
                          value={config.meta_access_token}
                          onChange={(e) => setConfig({ ...config, meta_access_token: e.target.value })}
                          placeholder="EAAxxxxxxx..."
                        />
                        <button
                          type="button"
                          onClick={() => toggleSecret('meta_access_token')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showSecrets.meta_access_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {config.provider === 'twilio' && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    🔴 Twilio WhatsApp
                  </CardTitle>
                  <CardDescription>
                    Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Twilio Console</a>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Account SID *</Label>
                      <Input
                        value={config.twilio_account_sid}
                        onChange={(e) => setConfig({ ...config, twilio_account_sid: e.target.value })}
                        placeholder="ACxxxxxxxxxxxxxxxx"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp Number *</Label>
                      <Input
                        value={config.twilio_whatsapp_number}
                        onChange={(e) => setConfig({ ...config, twilio_whatsapp_number: e.target.value })}
                        placeholder="+14155238886"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Auth Token *</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets.twilio_auth_token ? 'text' : 'password'}
                          value={config.twilio_auth_token}
                          onChange={(e) => setConfig({ ...config, twilio_auth_token: e.target.value })}
                          placeholder="xxxxxxxxxxxxxxxx"
                        />
                        <button
                          type="button"
                          onClick={() => toggleSecret('twilio_auth_token')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showSecrets.twilio_auth_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {config.provider === 'interakt' && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    💬 Interakt
                    <Badge variant="outline">India</Badge>
                  </CardTitle>
                  <CardDescription>
                    Get API key from <a href="https://app.interakt.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Interakt Dashboard</a>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>API Key *</Label>
                    <div className="relative">
                      <Input
                        type={showSecrets.interakt_api_key ? 'text' : 'password'}
                        value={config.interakt_api_key}
                        onChange={(e) => setConfig({ ...config, interakt_api_key: e.target.value })}
                        placeholder="xxxxxxxxxxxxxxxx"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSecret('interakt_api_key')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showSecrets.interakt_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {config.provider === 'aisensy' && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    🤖 AiSensy
                    <Badge variant="outline">India</Badge>
                  </CardTitle>
                  <CardDescription>
                    Get API key from <a href="https://app.aisensy.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">AiSensy Dashboard</a>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>API Key *</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets.aisensy_api_key ? 'text' : 'password'}
                          value={config.aisensy_api_key}
                          onChange={(e) => setConfig({ ...config, aisensy_api_key: e.target.value })}
                          placeholder="xxxxxxxxxxxxxxxx"
                        />
                        <button
                          type="button"
                          onClick={() => toggleSecret('aisensy_api_key')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showSecrets.aisensy_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Partner ID (Optional)</Label>
                      <Input
                        value={config.aisensy_partner_id}
                        onChange={(e) => setConfig({ ...config, aisensy_partner_id: e.target.value })}
                        placeholder="partner_id"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Notification Types Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>Choose which WhatsApp notifications to send automatically</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        ✅ Payment Confirmation
                      </p>
                      <p className="text-xs text-gray-500">When online payment is successful</p>
                    </div>
                    <Switch
                      checked={config.send_payment_confirmation}
                      onCheckedChange={(checked) => setConfig({ ...config, send_payment_confirmation: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        ✅ Payment Approval
                      </p>
                      <p className="text-xs text-gray-500">When offline payment is approved</p>
                    </div>
                    <Switch
                      checked={config.send_payment_approval}
                      onCheckedChange={(checked) => setConfig({ ...config, send_payment_approval: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        ❌ Payment Rejection
                      </p>
                      <p className="text-xs text-gray-500">When offline payment is rejected</p>
                    </div>
                    <Switch
                      checked={config.send_payment_rejection}
                      onCheckedChange={(checked) => setConfig({ ...config, send_payment_rejection: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        ⚠️ Expiry Reminder
                      </p>
                      <p className="text-xs text-gray-500">Before subscription expires</p>
                    </div>
                    <Switch
                      checked={config.send_expiry_reminder}
                      onCheckedChange={(checked) => setConfig({ ...config, send_expiry_reminder: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200 md:col-span-2">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        🎉 Welcome Message
                      </p>
                      <p className="text-xs text-gray-500">When new tenant is created</p>
                    </div>
                    <Switch
                      checked={config.send_welcome_message}
                      onCheckedChange={(checked) => setConfig({ ...config, send_welcome_message: checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Test WhatsApp
                </CardTitle>
                <CardDescription>Send a test message to verify your configuration</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="Enter phone number (e.g., 9876543210)"
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={handleTestMessage} disabled={testing} variant="outline">
                    {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Test
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Enter phone number with or without country code (91 will be added automatically)
                </p>
              </CardContent>
            </Card>

            {/* Message Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Message Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-[#e5ddd5] p-4 rounded-lg max-w-md">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="text-sm whitespace-pre-line">
                      ✅ <strong>Payment Successful!</strong>{'\n\n'}
                      Dear Customer,{'\n\n'}
                      Your payment of <strong>₹9,999.00</strong> has been received successfully.{'\n\n'}
                      📋 <strong>Details:</strong>{'\n'}
                      • Plan: Premium{'\n'}
                      • Valid Till: 2027-01-16{'\n\n'}
                      Thank you for your payment!{'\n\n'}
                      - Cloud Campus Team
                    </p>
                    <p className="text-xs text-gray-400 mt-2 text-right">12:30 PM ✓✓</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Broadcast Message
            </DialogTitle>
            <DialogDescription>
              Send a WhatsApp message to all tenants and groups
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Enter your message here...&#10;&#10;Use {name} for tenant name"
                rows={6}
              />
              <p className="text-xs text-gray-500">
                Tip: Use <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> to personalize with tenant name
              </p>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ This will send WhatsApp message to all registered tenants and groups with phone numbers.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)}>
              Cancel
            </Button>
            <Button onClick={handleBroadcast} disabled={broadcasting} className="bg-green-600 hover:bg-green-700">
              {broadcasting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
